import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Empty, Input, Select, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ALLY_STATUS_META } from '@goelectric/ui-components';
import type { AllyStatus, AllySummary, PageMeta, PersonType } from '@goelectric/shared-types';
import type { AllyFilters } from '../hooks/useAllies';
import { AllyStatusBadge, AllyTenantSyncBadge } from './AllyBadges';
import { DOCUMENT_TYPE_LABELS, PERSON_TYPE_LABELS, allyDisplayName, formatAllyDocument } from '../utils/format';

/** Debounce de la búsqueda antes de golpear el backend (server-side). */
const SEARCH_DEBOUNCE_MS = 300;

const PERSON_TYPE_OPTIONS: { value: PersonType; label: string }[] = (['JURIDICA', 'NATURAL'] as const).map(
  (value) => ({ value, label: PERSON_TYPE_LABELS[value] }),
);

const DOCUMENT_TYPE_OPTIONS = (['NIT', 'CC', 'CE'] as const).map((value) => ({
  value,
  label: DOCUMENT_TYPE_LABELS[value],
}));

const STATUS_OPTIONS: { value: AllyStatus; label: string }[] = (['ACTIVE', 'INACTIVE'] as const).map((value) => ({
  value,
  label: ALLY_STATUS_META[value].label,
}));

export interface AllyTableProps {
  allies: AllySummary[];
  meta: PageMeta;
  loading: boolean;
  error: string | null;
  filters: AllyFilters;
  /** Muestra el CTA del empty-state; solo con permiso de escritura. */
  canCreate: boolean;
  onFiltersChange: (filters: AllyFilters) => void;
  onOffsetChange: (offset: number) => void;
  onRetry: () => void;
  onCreate: () => void;
  onSelect: (allyId: string) => void;
}

function hasActiveFilters(filters: AllyFilters): boolean {
  return Boolean(
    filters.q || filters.personType || filters.documentType || filters.status || filters.documentNumber,
  );
}

/**
 * Tabla CRUD de Aliados (guía de estilo §8): búsqueda, filtros por columna,
 * paginación server-side (con el `meta` del contrato), empty-state con acción
 * sugerida y badges de estado + sincronización.
 */
export function AllyTable({
  allies,
  meta,
  loading,
  error,
  filters,
  canCreate,
  onFiltersChange,
  onOffsetChange,
  onRetry,
  onCreate,
  onSelect,
}: AllyTableProps) {
  const [query, setQuery] = useState(filters.q ?? '');

  // Sincroniza la búsqueda con el backend con debounce (evita una request por tecla).
  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = query.trim();
      if ((filters.q ?? '') !== next) {
        onFiltersChange({ ...filters, q: next || undefined });
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [query, filters, onFiltersChange]);

  const filtered = hasActiveFilters(filters);

  const columns = useMemo<ColumnsType<AllySummary>>(
    () => [
      {
        title: 'Aliado',
        key: 'ally',
        fixed: 'left',
        width: 260,
        render: (_, ally) => (
          <div className="crm-cell-stack">
            <Typography.Text strong title={allyDisplayName(ally)}>
              {allyDisplayName(ally)}
            </Typography.Text>
            <Typography.Text
              type="secondary"
              title={ally.id}
              style={{ fontFamily: 'var(--ge-font-mono)', fontSize: 12 }}
            >
              {ally.id}
            </Typography.Text>
          </div>
        ),
      },
      {
        title: 'Documento',
        key: 'document',
        width: 220,
        render: (_, ally) => (
          <div className="crm-cell-stack">
            <span title={formatAllyDocument(ally)} style={{ fontFamily: 'var(--ge-font-mono)' }}>
              {formatAllyDocument(ally)}
            </span>
            <Typography.Text
              type="secondary"
              title={`${DOCUMENT_TYPE_LABELS[ally.documentType]} · ${PERSON_TYPE_LABELS[ally.personType]}`}
              style={{ fontSize: 12 }}
            >
              {DOCUMENT_TYPE_LABELS[ally.documentType]} · {PERSON_TYPE_LABELS[ally.personType]}
            </Typography.Text>
          </div>
        ),
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        width: 220,
        ellipsis: true,
        render: (email: string) => email || '—',
      },
      {
        title: 'Comisión',
        dataIndex: 'commissionRatePct',
        key: 'commissionRatePct',
        width: 110,
        align: 'right',
        sorter: (a, b) => a.commissionRatePct - b.commissionRatePct,
        render: (value: number) => `${value}%`,
      },
      {
        title: 'Estado',
        dataIndex: 'status',
        key: 'status',
        width: 140,
        render: (status: AllyStatus) => <AllyStatusBadge status={status} />,
      },
      {
        title: 'Tenant',
        dataIndex: 'tenantSyncStatus',
        key: 'tenantSyncStatus',
        width: 150,
        render: (_, ally) => <AllyTenantSyncBadge status={ally.tenantSyncStatus} />,
      },
      {
        title: 'Acciones',
        key: 'actions',
        width: 90,
        align: 'right',
        render: (_, ally) => (
          <Button size="small" onClick={() => onSelect(ally.id)}>
            Ver
          </Button>
        ),
      },
    ],
    [onSelect],
  );

  const clearFilters = () => {
    setQuery('');
    onFiltersChange({});
  };

  return (
    <div>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="Buscar por nombre o documento"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          style={{ width: 280 }}
          aria-label="Buscar aliados"
        />
        <Select<PersonType | 'ALL'>
          value={filters.personType ?? 'ALL'}
          onChange={(value) =>
            onFiltersChange({ ...filters, personType: value === 'ALL' ? undefined : value })
          }
          style={{ width: 190 }}
          aria-label="Filtrar por tipo de persona"
          options={[{ value: 'ALL', label: 'Todas las personas' }, ...PERSON_TYPE_OPTIONS]}
        />
        <Select<(typeof DOCUMENT_TYPE_OPTIONS)[number]['value'] | 'ALL'>
          value={filters.documentType ?? 'ALL'}
          onChange={(value) =>
            onFiltersChange({ ...filters, documentType: value === 'ALL' ? undefined : value })
          }
          style={{ width: 200 }}
          aria-label="Filtrar por tipo de documento"
          options={[{ value: 'ALL', label: 'Todos los documentos' }, ...DOCUMENT_TYPE_OPTIONS]}
        />
        <Select<AllyStatus | 'ALL'>
          value={filters.status ?? 'ALL'}
          onChange={(value) =>
            onFiltersChange({ ...filters, status: value === 'ALL' ? undefined : value })
          }
          style={{ width: 170 }}
          aria-label="Filtrar por estado"
          options={[{ value: 'ALL', label: 'Todos los estados' }, ...STATUS_OPTIONS]}
        />
      </Space>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="No se pudieron cargar los aliados"
          description={error}
          action={
            <Button size="small" onClick={onRetry}>
              Reintentar
            </Button>
          }
        />
      ) : (
        <Table<AllySummary>
          rowKey="id"
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={allies}
          scroll={{ x: 1280 }}
          rowClassName="aliados-row"
          onRow={(ally) => ({ onClick: () => onSelect(ally.id) })}
          pagination={{
            current: Math.floor(meta.offset / Math.max(meta.limit, 1)) + 1,
            pageSize: meta.limit,
            total: meta.total,
            showSizeChanger: false,
            showTotal: (total) => `${total} aliados`,
            onChange: (page) => onOffsetChange((page - 1) * meta.limit),
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={filtered ? 'Sin aliados para los filtros' : 'Aún no hay aliados'}
              >
                {filtered ? (
                  <Button onClick={clearFilters}>Limpiar filtros</Button>
                ) : (
                  canCreate && (
                    <Button type="primary" onClick={onCreate}>
                      Crear el primer aliado
                    </Button>
                  )
                )}
              </Empty>
            ),
          }}
        />
      )}
    </div>
  );
}
